import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Globe } from 'lucide-react';
import { COMPANY } from '../constants';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from './ui/button';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, navLinks, homePath, altPath, lang } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleNavClick = (e, href) => {
    const hashIndex = href.indexOf('#');
    if (hashIndex !== -1) {
      e.preventDefault();
      const targetId = href.substring(hashIndex + 1);
      const basePath = href.substring(0, hashIndex);

      // If we're not on the right base page, navigate there
      if (location.pathname !== basePath && location.pathname !== basePath + '/') {
        window.location.href = href;
        return;
      }

      const element = document.getElementById(targetId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md border-b border-gray-100'
          : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link
            to={homePath}
            className="flex items-center gap-2 flex-shrink-0"
            data-testid="logo-link"
          >
            <div className="flex items-center bg-white px-1 rounded">
              <span className="text-2xl sm:text-3xl font-black tracking-tight text-[#41DB23]">reco</span>
              <span className="text-2xl sm:text-3xl font-black tracking-tight text-black">pneus</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-medium text-gray-700 hover:text-[#41DB23] transition-colors"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Right side: Lang switcher + CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Switcher */}
            <Link
              to={altPath}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-full text-xs font-semibold text-gray-600 hover:border-[#41DB23] hover:text-[#41DB23] transition-colors"
              data-testid="lang-switcher-desktop"
              title={lang === 'fr' ? 'Switch to English' : 'Passer en français'}
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === 'fr' ? 'EN' : 'FR'}
            </Link>

            <a href={COMPANY.phoneLink} data-testid="header-phone-cta">
              <Button
                className="bg-[#41DB23] text-white hover:bg-[#38C41E] font-bold uppercase text-sm tracking-wide px-4 py-2 rounded-md flex items-center gap-2 shadow-md"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">{t.callNow}</span>
                <span className="sm:hidden">{t.callNowShort}</span>
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-[#41DB23] transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label={isMobileMenuOpen ? t.menuClose : t.menuOpen}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden bg-white border-t border-gray-100 shadow-lg"
          data-testid="mobile-menu"
        >
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="py-3 px-4 text-base font-medium text-gray-700 hover:text-[#41DB23] hover:bg-green-50 rounded-md transition-colors"
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}

            {/* Mobile Language Switcher */}
            <Link
              to={altPath}
              className="py-3 px-4 text-base font-medium text-gray-700 hover:text-[#41DB23] hover:bg-green-50 rounded-md transition-colors inline-flex items-center gap-2"
              data-testid="lang-switcher-mobile"
            >
              <Globe className="w-4 h-4" />
              {lang === 'fr' ? 'English' : 'Français'}
            </Link>

            <a
              href={COMPANY.phoneLink}
              className="mt-4"
              data-testid="mobile-phone-cta"
            >
              <Button
                className="w-full bg-[#41DB23] text-white hover:bg-[#38C41E] font-bold uppercase text-sm tracking-wide py-3 rounded-md flex items-center justify-center gap-2 shadow-md"
              >
                <Phone className="w-4 h-4" />
                {t.callNow} — {COMPANY.phone}
              </Button>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
